import axios from 'axios';
import { resetSessionTimer } from './sessionTimeout';
// Use relative URL so it works in both dev and production
const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'http://127.0.0.1:3001'
    : ''; // Empty string means relative to current origin (https://sharpshot.app)
// Helper function to parse JSON with NaN values
const parseJsonWithNaN = (text) => {
    // Replace NaN with null in JSON string
    const cleanedText = text.replace(/:\s*NaN/g, ': null');
    return JSON.parse(cleanedText);
};
// Helper function to check if a game has complete data
const isCompleteGame = (prediction) => {
    // Check basic info first
    const hasBasicInfo = prediction.home_team && prediction.away_team && prediction.game_id;
    if (!hasBasicInfo)
        return false;
    // Check for valid spread - handle both old and new CFB structures
    let hasValidSpread = false;
    if (prediction.spread_prediction?.spread_value != null) {
        // Old MLB structure
        hasValidSpread = !isNaN(prediction.spread_prediction.spread_value) &&
            Math.abs(prediction.spread_prediction.spread_value) <= 50;
    }
    else if (prediction.spread != null) {
        // New CFB structure
        hasValidSpread = !isNaN(prediction.spread) &&
            Math.abs(prediction.spread) <= 50;
    }
    else if (prediction.betting_analysis?.point_spread != null) {
        // CFB betting analysis structure
        hasValidSpread = !isNaN(prediction.betting_analysis.point_spread) &&
            Math.abs(prediction.betting_analysis.point_spread) <= 50;
    }
    // Check for valid probabilities - handle both structures
    let hasValidProbabilities = false;
    if (prediction.spread_prediction?.home_covers_spread_prob != null) {
        // Old MLB structure
        hasValidProbabilities = !isNaN(prediction.spread_prediction.home_covers_spread_prob) &&
            prediction.spread_prediction.home_covers_spread_prob >= 0 &&
            prediction.spread_prediction.home_covers_spread_prob <= 1;
    }
    else if (prediction.prediction?.home_cover_probability != null) {
        // New CFB structure
        hasValidProbabilities = !isNaN(prediction.prediction.home_cover_probability) &&
            prediction.prediction.home_cover_probability >= 0 &&
            prediction.prediction.home_cover_probability <= 1;
    }
    else if (prediction.prediction?.home_probability != null) {
        // CFB prediction structure
        hasValidProbabilities = !isNaN(prediction.prediction.home_probability) &&
            prediction.prediction.home_probability >= 0 &&
            prediction.prediction.home_probability <= 1;
    }
    else if (prediction.prediction?.pick_probability != null) {
        // NEW CFB schema: pick_probability
        hasValidProbabilities = !isNaN(prediction.prediction.pick_probability) &&
            prediction.prediction.pick_probability >= 0 &&
            prediction.prediction.pick_probability <= 1;
    }
    return hasValidSpread && hasValidProbabilities;
};
// Transform the new JSON structure to match the expected frontend format
const transformPredictionsData = (data) => {
    // If it already has 'games' array, return as-is
    if (data.games) {
        return data;
    }
    // If it has 'predictions' array, transform it
    if (data.predictions) {
        // Filter out incomplete games first
        const completeGames = data.predictions.filter(isCompleteGame);
        const games = completeGames.map((prediction) => ({
            id: prediction.game_id,
            home_team: prediction.home_team,
            away_team: prediction.away_team,
            week: data.week || prediction.week,
            year: data.season || prediction.year || 2025,
            spread_line: prediction.betting_analysis?.point_spread || prediction.spread_prediction?.spread_value || prediction.spread || prediction.spread_line,
            ensemble_probability: (() => {
                // NEW SCHEMA: Use pick_probability directly
                if (prediction.prediction?.pick_probability) {
                    return prediction.prediction.pick_probability;
                }
                // Handle different legacy data structures
                if (prediction.legacy_compatibility?.ensemble_probability) {
                    return prediction.legacy_compatibility.ensemble_probability;
                }
                if (prediction.ensemble_probability) {
                    return prediction.ensemble_probability;
                }
                if (prediction.spread_prediction?.home_covers_spread_prob) {
                    return prediction.spread_prediction.home_covers_spread_prob;
                }
                if (prediction.prediction?.home_cover_probability) {
                    return prediction.prediction.home_cover_probability;
                }
                if (prediction.prediction?.home_probability) {
                    return prediction.prediction.home_probability;
                }
                return 0.6; // fallback
            })(),
            confidence_level: (() => {
                // Handle new CFB structure
                if (prediction.prediction?.confidence_tier) {
                    const tier = prediction.prediction.confidence_tier;
                    return tier; // Should already be in correct format (HIGH, MEDIUM, etc.)
                }
                // Use new betting analysis confidence tier if available
                if (prediction.betting_analysis?.best_bet_recommendation?.confidence_tier) {
                    const tier = prediction.betting_analysis.best_bet_recommendation.confidence_tier;
                    // Convert new tier names to standard format
                    if (tier === 'HIGH_CONFIDENCE_BET')
                        return 'HIGH';
                    if (tier === 'MEDIUM_CONFIDENCE_BET')
                        return 'MEDIUM_PLUS';
                    return tier.replace('_BET', '').replace('_CONFIDENCE', '');
                }
                // Fallback to legacy fields
                return prediction.recommended_bet?.confidence_level || prediction.confidence || 'MEDIUM';
            })(),
            team_to_cover: (() => {
                // NEW SCHEMA: Use explicit pick_team field
                if (prediction.prediction?.pick_team) {
                    return prediction.prediction.pick_team;
                }
                // Handle legacy CFB structure
                if (prediction.betting_analysis?.bet_side) {
                    const side = prediction.betting_analysis.bet_side;
                    return side === 'HOME' ? prediction.home_team : prediction.away_team;
                }
                // Use new betting analysis recommendation if available
                if (prediction.betting_analysis?.best_bet_recommendation?.recommendation === 'PLACE_BET') {
                    const team = prediction.betting_analysis.best_bet_recommendation.bet_details?.team;
                    return team === 'HOME' ? prediction.home_team : prediction.away_team;
                }
                // Handle NFL prediction structure
                if (prediction.prediction?.home_covers === true) {
                    return prediction.home_team;
                }
                else if (prediction.prediction?.home_covers === false) {
                    return prediction.away_team;
                }
                // Handle CFB prediction structure
                if (prediction.prediction?.home_covers_spread === "True") {
                    return prediction.home_team;
                }
                else if (prediction.prediction?.home_covers_spread === "False") {
                    return prediction.away_team;
                }
                // Fallback to legacy ensemble prediction
                const ensemblePred = prediction.legacy_compatibility?.ensemble_prediction || prediction.ensemble_prediction;
                return ensemblePred === 1 ? prediction.home_team : prediction.away_team;
            })(),
            display_confidence: (() => {
                // NEW SCHEMA: Use pick_probability directly (already the confidence for our pick)
                if (prediction.prediction?.pick_probability) {
                    return prediction.prediction.pick_probability * 100;
                }
                // Handle different legacy probability structures
                let prob = 0.6; // fallback
                if (prediction.spread_prediction?.home_covers_spread_prob) {
                    prob = prediction.spread_prediction.home_covers_spread_prob;
                }
                else if (prediction.prediction?.home_cover_probability) {
                    prob = prediction.prediction.home_cover_probability;
                }
                else if (prediction.prediction?.home_probability) {
                    // CFB structure uses home_probability
                    prob = prediction.prediction.home_probability;
                }
                else if (prediction.prediction_confidence) {
                    prob = prediction.prediction_confidence;
                }
                else if (prediction.confidence_score) {
                    prob = prediction.confidence_score;
                }
                return Math.max(prob, 1 - prob) * 100; // Use the higher probability
            })(),
            individual_predictions: (() => {
                // Handle new CFB structure - it doesn't have individual model probabilities in the same way
                // CFB uses dynamic analysis instead of individual model predictions
                if (prediction.dynamic_analysis) {
                    // Create placeholder values based on dynamic analysis
                    const homeRating = prediction.dynamic_analysis.home_overall_rating || 85;
                    const awayRating = prediction.dynamic_analysis.away_overall_rating || 85;
                    const homePct = homeRating / (homeRating + awayRating);
                    return {
                        xgboost: homePct,
                        lightgbm: homePct * 0.95, // slight variation
                        random_forest: homePct * 1.05, // slight variation
                        gradient_boosting: homePct,
                        svm: 0,
                        neuralNetworkSmall: 0,
                        neuralNetworkDeep: 0,
                        neuralNetworkWide: 0,
                        pytorchNeuralNet: 0
                    };
                }
                // Handle MLB structure with spread_prediction.individual_models
                if (prediction.spread_prediction?.individual_models) {
                    const models = prediction.spread_prediction.individual_models;
                    return {
                        xgboost: models.xgboost || 0,
                        lightgbm: models.lightgbm || 0,
                        random_forest: models.random_forest || 0,
                        gradient_boosting: models.gradient_boosting || 0,
                        svm: 0,
                        neuralNetworkSmall: 0,
                        neuralNetworkDeep: 0,
                        neuralNetworkWide: 0,
                        pytorchNeuralNet: 0
                    };
                }
                // Fallback to legacy structure
                return prediction.legacy_compatibility?.individual_probabilities || prediction.individual_predictions || {};
            })(),
            conference: (() => {
                // For CFB, use team conference data if available
                const homeConference = prediction.team_analysis?.home_team_stats?.conference;
                const awayConference = prediction.team_analysis?.away_team_stats?.conference;
                // If both teams are from the same conference, use that conference
                if (homeConference && awayConference && homeConference === awayConference) {
                    return homeConference;
                }
                // For different conferences, prioritize major conferences for filtering
                const majorConferences = ['SEC', 'Big Ten', 'Big 12', 'ACC', 'Pac-12'];
                if (homeConference && majorConferences.includes(homeConference)) {
                    return homeConference;
                }
                if (awayConference && majorConferences.includes(awayConference)) {
                    return awayConference;
                }
                // Otherwise use the home team's conference for consistency
                return prediction.conference || homeConference || awayConference || null;
            })(),
            is_conference_game: prediction.is_conference_game || false,
            game_time: prediction.game_date,
            venue: prediction.venue,
            // For game analysis compatibility
            predicted_team: (() => {
                // NEW SCHEMA: Use explicit pick_team field
                if (prediction.prediction?.pick_team) {
                    return prediction.prediction.pick_team;
                }
                // Handle legacy CFB structure
                if (prediction.betting_analysis?.bet_side) {
                    const side = prediction.betting_analysis.bet_side;
                    return side === 'HOME' ? prediction.home_team : prediction.away_team;
                }
                if (prediction.betting_analysis?.best_bet_recommendation?.recommendation === 'PLACE_BET') {
                    const team = prediction.betting_analysis.best_bet_recommendation.bet_details?.team;
                    return team === 'HOME' ? prediction.home_team : prediction.away_team;
                }
                // Handle NFL prediction structure
                if (prediction.prediction?.home_covers === true) {
                    return prediction.home_team;
                }
                else if (prediction.prediction?.home_covers === false) {
                    return prediction.away_team;
                }
                // Handle CFB prediction structure
                if (prediction.prediction?.home_covers_spread === "True") {
                    return prediction.home_team;
                }
                else if (prediction.prediction?.home_covers_spread === "False") {
                    return prediction.away_team;
                }
                const ensemblePred = prediction.legacy_compatibility?.ensemble_prediction || prediction.ensemble_prediction;
                return ensemblePred === 1 ? prediction.home_team : prediction.away_team;
            })(),
            // Store the full raw data for game analysis
            raw_data: prediction,
            original_data: {
                // NEW SCHEMA: Use real team statistics
                home_points: prediction.team_analysis?.home_team_stats?.scoring_offense || 28,
                away_points: prediction.team_analysis?.away_team_stats?.scoring_offense || 24,
                home_record: prediction.team_analysis?.home_team_stats?.overall_record || '8-4',
                away_record: prediction.team_analysis?.away_team_stats?.overall_record || '7-5'
            },
            // Map betting indicators if available
            betting_indicators: prediction.betting_indicators ? {
                betting_tier: prediction.betting_indicators.betting_tier,
                tier_reason: prediction.betting_indicators.tier_reason,
                bankroll_percentage: prediction.betting_indicators.bankroll_percentage,
                model_agreement_score: prediction.betting_indicators.model_agreement_score,
                unanimous_direction: prediction.betting_indicators.unanimous_direction,
                agreeing_models: prediction.betting_indicators.agreeing_models,
                edge_strength: prediction.betting_indicators.edge_strength,
                expected_roi_110: prediction.betting_indicators.expected_roi_110,
                pick_probability: prediction.betting_indicators.pick_probability,
                recommendation: prediction.betting_indicators.recommendation,
                bet_ranking: prediction.betting_indicators.bet_ranking ? {
                    composite_score: prediction.betting_indicators.bet_ranking.composite_score,
                    grade: prediction.betting_indicators.bet_ranking.grade,
                    strengths: prediction.betting_indicators.bet_ranking.strengths || [],
                    component_scores: prediction.betting_indicators.bet_ranking.component_scores || {
                        edge_score: 0,
                        ev_score: 0,
                        consensus_score: 0,
                        probability_score: 0,
                        odds_score: 0
                    },
                    overall_rank: prediction.betting_indicators.bet_ranking.overall_rank,
                    grade_rank: prediction.betting_indicators.bet_ranking.grade_rank,
                    total_opportunities: prediction.betting_indicators.bet_ranking.total_opportunities
                } : {
                    composite_score: 0,
                    grade: 'C',
                    strengths: [],
                    component_scores: { edge_score: 0, ev_score: 0, consensus_score: 0, probability_score: 0, odds_score: 0 },
                    overall_rank: 999,
                    grade_rank: 999,
                    total_opportunities: 91
                }
            } : undefined
        }));
        return {
            metadata: {
                week: data.week,
                season: data.season,
                sport: data.sport,
                total_games: games.length, // Use filtered count
                high_confidence_games: games.filter((g) => g.display_confidence >= 80).length,
                betting_recommendations: data.betting_recommendations,
                average_confidence: data.average_confidence,
                enhancement_info: data.enhancement_info,
                betting_summary: data.betting_summary,
                generated_at: data.generated_at,
                prediction_date: data.prediction_date,
                system_version: data.system_version,
                // Add info about filtered games
                original_total_games: data.total_games,
                filtered_games: data.total_games - games.length
            },
            games: games
        };
    }
    // Fallback - return data as-is if structure is unexpected
    return data;
};
const api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json',
    },
});
// Add request interceptor to include auth token and reset session timer
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('sharpshot_auth_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        // Reset session timer on any authenticated API call
        resetSessionTimer();
    }
    return config;
});
// Add response interceptor for error handling
api.interceptors.response.use((response) => response, (error) => {
    console.error('API Error:', error);
    if (error.response?.status === 401) {
        // Token expired or invalid, clear storage and redirect
        localStorage.removeItem('sharpshot_auth_token');
        localStorage.removeItem('sharpshot_user');
        window.location.href = '/login';
    }
    return Promise.reject(error);
});
export const fetchPredictions = async (sportId = 'cfb') => {
    try {
        // Add cache busting parameter to force fresh data
        const timestamp = Date.now();
        // Handle sport-specific file naming conventions
        let predictionsUrl;
        if (sportId === 'mlb') {
            predictionsUrl = `/static/mlb/mlb_predictions.json?v=${timestamp}`;
        }
        else if (sportId === 'nfl') {
            predictionsUrl = `/static/nfl/nfl_predictions.json?v=${timestamp}`;
        }
        else if (sportId === 'cfb') {
            predictionsUrl = `/static/cfb/cfb_predictions.json?v=${timestamp}`;
        }
        else {
            predictionsUrl = `/static/${sportId}/${sportId}_predictions.json?v=${timestamp}`;
        }
        const response = await fetch(predictionsUrl);
        if (!response.ok) {
            // Fallback to CFB if sport-specific file doesn't exist
            if (sportId !== 'cfb') {
                const fallbackResponse = await fetch(`/static/cfb/cfb_predictions.json?v=${timestamp}`);
                if (fallbackResponse.ok) {
                    const fallbackText = await fallbackResponse.text();
                    const fallbackData = parseJsonWithNaN(fallbackText);
                    return transformPredictionsData(fallbackData);
                }
            }
            throw new Error('Failed to fetch predictions from static folder');
        }
        const text = await response.text();
        const data = parseJsonWithNaN(text);
        return transformPredictionsData(data);
    }
    catch (error) {
        console.error('Failed to fetch predictions:', error);
        throw error;
    }
};
export const fetchGameAnalysis = async (gameId, sportId = 'cfb') => {
    try {
        // First try to fetch predictions to get the game data
        const predictions = await fetchPredictions(sportId);
        const game = predictions.games.find(g => g.id === gameId);
        if (!game) {
            throw new Error(`Game ${gameId} not found`);
        }
        // Load profitability data for context
        let profitabilityResponse;
        try {
            profitabilityResponse = await fetch(`/static/${sportId}/profitability_summary.json`);
            if (!profitabilityResponse.ok && sportId !== 'cfb') {
                // Fallback to CFB profitability data
                profitabilityResponse = await fetch('/static/cfb/profitability_summary.json');
            }
        }
        catch {
            profitabilityResponse = await fetch('/static/cfb/profitability_summary.json');
        }
        const profitabilityData = await profitabilityResponse.json();
        return {
            game: game,
            profitabilityData: profitabilityData
        };
    }
    catch (error) {
        console.error('Failed to fetch game analysis:', error);
        throw error;
    }
};
// Fetch betting strategy metrics from profitability folder
export const fetchBettingStrategyMetrics = async (sportId = 'cfb') => {
    try {
        // Try sport-specific metrics first
        let response = await fetch(`/static/${sportId}/profitability/kelly_criterion_metrics_20250701_214241.json`);
        if (!response.ok && sportId !== 'cfb') {
            // Fallback to CFB metrics
            response = await fetch('/static/cfb/profitability/kelly_criterion_metrics_20250701_214241.json');
        }
        if (!response.ok) {
            throw new Error('Failed to fetch betting strategy metrics');
        }
        return await response.json();
    }
    catch (error) {
        console.error('Failed to fetch betting strategy metrics:', error);
        throw error;
    }
};
// Stripe API functions
export const createCheckoutSession = async (planId, userId) => {
    try {
        const response = await api.post('/api/stripe/create-checkout-session', {
            planId,
            userId,
            successUrl: `${window.location.origin}/dashboard/subscription/success`,
            cancelUrl: `${window.location.origin}/dashboard/plans`
        });
        return response.data;
    }
    catch (error) {
        console.error('Failed to create checkout session:', error);
        throw error;
    }
};
export const createPortalSession = async (userId) => {
    try {
        const response = await api.post('/api/stripe/create-portal-session', {
            userId,
            returnUrl: `${window.location.origin}/dashboard/profile`
        });
        return response.data;
    }
    catch (error) {
        console.error('Failed to create portal session:', error);
        throw error;
    }
};
export const fetchSubscriptionPlans = async () => {
    try {
        const response = await api.get('/api/stripe/plans');
        return response.data;
    }
    catch (error) {
        console.error('Failed to fetch subscription plans:', error);
        throw error;
    }
};
// Sports API functions
export const fetchSportsConfig = async () => {
    try {
        const response = await api.get('/api/sports');
        return response.data;
    }
    catch (error) {
        console.error('Failed to fetch sports config:', error);
        throw error;
    }
};
// Game Results API functions
export const fetchGameResults = async (sport) => {
    try {
        const response = await api.get(`/api/game-results/${sport.toLowerCase()}`);
        return response.data;
    }
    catch (error) {
        console.error(`Failed to fetch game results for ${sport}:`, error);
        return null; // Return null if results not found (e.g., no games completed yet)
    }
};
// Merge game results with predictions
export const enrichPredictionsWithResults = async (predictions, sport) => {
    try {
        const resultsResponse = await fetchGameResults(sport);
        if (!resultsResponse || !resultsResponse.data) {
            console.log('🔍 No results data available for enrichment');
            return predictions; // Return original predictions if no results available
        }
        const gameResults = resultsResponse.data.game_results || [];
        console.log(`🔍 Enrichment: Found ${gameResults.length} game results for ${sport}`);
        // Create a map of game results by game ID for quick lookup
        const resultsMap = new Map();
        gameResults.forEach((result) => {
            console.log(`🔍 Mapping result: ${result.game_id} -> final: ${result.final}, score: ${result.away_score}-${result.home_score}`);
            resultsMap.set(result.game_id, result);
        });
        console.log(`🔍 Predictions has ${predictions.games.length} games to enrich`);
        // Enrich each game with its result and bet outcome
        const enrichedGames = predictions.games.map(game => {
            console.log(`🔍 Looking for result for game.id: ${game.id}, game.game_id: ${game.game_id}`);
            const result = resultsMap.get(game.id.toString());
            if (!result) {
                console.log(`🔍 No result found for game ${game.id}`);
                return game; // No result found, return original game
            }
            console.log(`🔍 ✅ MATCH FOUND! Enriching ${game.id} with result:`, result);
            const enrichedGame = {
                ...game,
                result: result.final ? {
                    home_score: result.home_score,
                    away_score: result.away_score,
                    winner: (result.winner === result.home_team ? 'home' : 'away'),
                    final: result.final,
                    status: result.status
                } : undefined,
                bet_outcome: result.user_bet ? {
                    bet_id: result.user_bet.bet_id,
                    status: result.user_bet.status,
                    payout: result.user_bet.payout,
                    bet_won: result.user_bet.status === 'won',
                    bet_placed: true
                } : undefined,
                // Include spread coverage data for accurate prediction tracking
                spread_coverage: result.spread_coverage,
                prediction_correct: result.prediction_correct,
                moneyline_correct: result.moneyline_correct
            };
            console.log(`🔍 Enriched game:`, enrichedGame);
            return enrichedGame;
        });
        return {
            ...predictions,
            games: enrichedGames
        };
    }
    catch (error) {
        console.error('Error enriching predictions with results:', error);
        return predictions; // Return original predictions on error
    }
};
